import { Component, Element, Host, h, Prop, State } from '@stencil/core';

@Component({
  tag: 'me-separated-layout',
  styleUrl: 'me-separated-layout.scss',
  shadow: false,
})
export class MeSeparatedLayout {

  @Element() host: HTMLElement;

  @Prop() size = 'r';
  @Prop() center = false;

  private separatorTemplate = document.createElement('template');
  private contentTemplate = document.createElement('template');
  private contentNodes: Node[] = [];

  /**
   * The Content Version
   *
   * Allows to force a re-render cycle of the component
   */
  @State() private contentVersion = 0;

  private contentObserver = new MutationObserver(this.handleContentChange.bind(this));

  componentDidLoad() {
    setTimeout(() => this.contentVersion++);
    this.observeTemplateSubtree(this.contentTemplate);
  }

  render() {
    if (this.contentNodes.length > 0) {
      this.contentTemplate.content.replaceChildren(...this.contentNodes);
    }

    const classList = [
      'me-separated-layout',
      `me-separated-layout--${this.size}`,
    ].filter(c => !!c).join(' ');

    const classListContent = [
      'me-separated-layout__content',
      this.center ? 'me-separated-layout__content--center' : null,
    ].filter(c => !!c).join(' ');

    return (
      <Host class={classList}>
        <template ref={(el: HTMLTemplateElement) => this.separatorTemplate = el}>
          <slot name="separator" />
        </template>
        <template ref={(el: HTMLTemplateElement) => this.contentTemplate = el}>
          <slot />
        </template>
        <div
          class={classListContent}
          ref={el => el.replaceChildren(...this.separatedContent())}
        />
      </Host>
    );
  }

  private childNodes(template: HTMLTemplateElement): Node[] {
    return Array.from(
      template.content.childNodes.length
        ? template.content.childNodes
        : template.childNodes);
  }

  private separatedContent(): Node[] {
    const separatorNodes = this.childNodes(this.separatorTemplate)
      .filter(el => el.nodeType === Node.ELEMENT_NODE);
    this.contentNodes = this.childNodes(this.contentTemplate)
      .filter(el => el.nodeType === Node.ELEMENT_NODE);
    const separatedContent = [];

    const separator = document.createElement('div');
    separator.classList.add('me-separated-layout__separator');
    separator.replaceChildren(...separatorNodes.map(el => el.cloneNode(true)));

    this.contentNodes.forEach((node, index) => {
      const item = document.createElement('div');
      item.classList.add('me-separated-layout__item');
      item.appendChild(node);
      separatedContent.push(item);

      if (index < this.contentNodes.length - 1) {
        item.appendChild(separator.cloneNode(true));
      }
    });

    return separatedContent;
  }

  private handleContentChange() {
    this.contentVersion++; // force re-rendering of the component
  }

  private observeTemplateSubtree(template: HTMLTemplateElement) {
    this.contentObserver.observe(template, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
  }
}
